import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { generateScene, downloadImage } from '../lib/openaiService';
import { getCategories, getTargetById, generateRandomPosition } from '../lib/targetLibrary';
import Stepper, { Step } from './Stepper';
import { Search, ArrowLeft, Clock, Users, Trophy, Loader2, Sparkles, Copy, Check, Eye, RefreshCw, Crosshair, X } from 'lucide-react';
import TextRevealButton from './TextRevealButton';
import NeonCountdown from './NeonCountdown';

/**
 * FindImageGame - Standalone "Find the Image" mini-game
 * 
 * Three views:
 * 1. Setup (host only) - Create game, generate image, detect target
 * 2. Play (all players) - Race to find the hidden target
 * 3. Results - Show winner and reveal target location
 */
function FindImageGame({ onBack, backRef, initialJoinCode, initialGameId }) {
  const { user, userProfile, isHost } = useAuth();

  // Navigation state
  const [view, setView] = useState('lobby'); // 'lobby', 'setup', 'waiting', 'play', 'results'
  
  // Game state
  const [game, setGame] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerEmail, setPlayerEmail] = useState('');
  
  // Setup state
  const [sceneName, setSceneName] = useState('');
  const [scenePrompt, setScenePrompt] = useState('');
  // targetObject is now derived from selectedTarget in the library
  const [timeLimit, setTimeLimit] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  
  // New stepper-based setup state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [generatedImagePath, setGeneratedImagePath] = useState(null);
  const [targetPosition, setTargetPosition] = useState(null);
  const [setupStep, setSetupStep] = useState(1);
  const [regenerationsUsed, setRegenerationsUsed] = useState(0);
  const MAX_REGENERATIONS = 1;
  const [draftGameId, setDraftGameId] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [existingDraft, setExistingDraft] = useState(null);

  // Lobby hero: cursor position and crosshair (same as home page Find the Image showcase)
  const lobbyHeroRef = useRef(null);
  const [fiLobbyCursorPos, setFiLobbyCursorPos] = useState({ x: 0, y: 0 });
  const [fiLobbyShowCrosshair, setFiLobbyShowCrosshair] = useState(false);

  // Expose back handler to parent via ref
  useEffect(() => {
    if (backRef) {
      backRef.current = {
        handleBack: () => {
          if (view === 'setup' && setupStep > 1) {
            setSetupStep(setupStep - 1);
            return true; // handled internally
          } else if (view === 'setup' && setupStep === 1) {
            setView('lobby');
            return true;
          } else if (view === 'lobby') {
            return false; // let parent handle (leave game)
          }
          return false;
        }
      };
    }
  }, [backRef, view, setupStep]);

  // Auto-join if redirected from main join page with a Find Image code
  const hasAutoJoined = useRef(false);
  useEffect(() => {
    if (initialJoinCode && user && userProfile && !hasAutoJoined.current) {
      hasAutoJoined.current = true;
      setJoinCodeInput(initialJoinCode);
      // Trigger join after state update
      setTimeout(() => {
        const joinAsync = async () => {
          try {
            const code = initialJoinCode.toUpperCase().trim();
            const { data: gameData, error: findError } = await supabase
              .from('find_image_games')
              .select('*')
              .eq('join_code', code)
              .maybeSingle();

            if (findError || !gameData) return;
            if (gameData.game_status === 'finished' || gameData.game_status === 'active') return;

            const { error: joinErr } = await supabase
              .from('find_image_participants')
              .insert({
                game_id: gameData.id,
                user_id: user.id,
                player_name: userProfile?.full_name || user.email,
                email: user.email
              });

            if (joinErr && joinErr.code !== '23505') return;

            setGame(gameData);
            setView('waiting');
          } catch (err) {
            console.error('[FIND-IMAGE] Auto-join error:', err);
          }
        };
        joinAsync();
      }, 0);
    }
  }, [initialJoinCode, user, userProfile]);

  // When opened from My Games with a selected game: load that game and go to the right view
  const lastOpenedGameIdRef = useRef(null);
  useEffect(() => {
    if (!initialGameId) {
      lastOpenedGameIdRef.current = null;
      return;
    }
    if (!user) return;
    if (lastOpenedGameIdRef.current === initialGameId) return;
    lastOpenedGameIdRef.current = initialGameId;

    const openSelectedGame = async () => {
      try {
        const { data: gameData, error } = await supabase
          .from('find_image_games')
          .select('*')
          .eq('id', initialGameId)
          .maybeSingle();

        if (error || !gameData) return;

        const status = gameData.game_status || 'setup';
        if (status === 'setup') {
          handleResumeDraft(gameData);
          return;
        }
        if (status === 'ready') {
          const { data: participantsData } = await supabase
            .from('find_image_participants')
            .select('*')
            .eq('game_id', gameData.id);
          setParticipants(participantsData || []);
          setGame(gameData);
          setView('waiting');
          return;
        }
        if (status === 'active') {
          setGame(gameData);
          setView('play');
          return;
        }
        if (status === 'finished') {
          setGame(gameData);
          setView('results');
        }
      } catch (err) {
        console.warn('[FIND-IMAGE] Error opening selected game:', err);
      }
    };

    openSelectedGame();
  }, [initialGameId, user]);

  // Check for existing draft games when host loads the page (only if no initialGameId)
  useEffect(() => {
    if (!user || !isHost() || initialGameId) return;
    const checkForDrafts = async () => {
      try {
        const { data, error } = await supabase
          .from('find_image_games')
          .select('*')
          .eq('created_by', user.id)
          .eq('game_status', 'setup')
          .order('updated_at', { ascending: false })
          .limit(1);

        if (!error && data && data.length > 0) {
          setExistingDraft(data[0]);
        }
      } catch (err) {
        console.warn('[FIND-IMAGE] Error checking drafts:', err);
      }
    };
    checkForDrafts();
  }, [user, initialGameId]);

  const handleResumeDraft = (draft) => {
    setDraftGameId(draft.id);
    setSceneName(draft.game_name || '');
    setScenePrompt(draft.scene_prompt || '');
    setGeneratedImageUrl(draft.image_url || null);
    setGeneratedImagePath(draft.image_path || null);
    setTimeLimit(draft.time_limit_seconds || 60);

    if (draft.target_object) {
      const categories = getCategories();
      const allTargets = categories.flatMap(cat => cat.targets);
      const match = allTargets.find(t => `${t.emoji} ${t.name}` === draft.target_object);
      if (match) {
        setSelectedTarget(match.id);
        setSelectedCategory(categories.find(cat => cat.targets.some(t => t.id === match.id))?.id || null);
      }
    }
    if (draft.target_bbox) {
      setTargetPosition(draft.target_bbox);
    }

    const step = draft.setup_step || 1;
    setSetupStep(step);
    setExistingDraft(null);
    setView('setup');
  };

  // Play state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [gameCursorPos, setGameCursorPos] = useState({ x: 0, y: 0 });
  const [showGameCrosshair, setShowGameCrosshair] = useState(false);
  const gameAreaRef = useRef(null);

  // UI state
  const [copied, setCopied] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  
  // Refs
  const imageRef = useRef(null);
  const timerRef = useRef(null);
  const channelRef = useRef(null);

  // Generate a 6-char join code
  const generateJoinCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // ==================== REAL-TIME SUBSCRIPTIONS ====================

  useEffect(() => {
    if (!game?.id) return;

    const channel = supabase
      .channel(`find-image-${game.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'find_image_games',
        filter: `id=eq.${game.id}`
      }, (payload) => {
        const updated = payload.new;
        console.log('[FIND-IMAGE] Game updated:', updated.game_status);
        setGame(updated);

        if (updated.game_status === 'active' && view === 'waiting') {
          setView('play');
        }
        if (updated.game_status === 'finished') {
          setView('results');
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'find_image_participants',
        filter: `game_id=eq.${game.id}`
      }, (payload) => {
        console.log('[FIND-IMAGE] Player joined:', payload.new.player_name);
        setParticipants(prev => [...prev, payload.new]);
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'find_image_participants',
        filter: `game_id=eq.${game.id}`
      }, (payload) => {
        setParticipants(prev => prev.filter(p => p.id !== payload.old.id));
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [game?.id, view]);

  // Load participants when game is set
  useEffect(() => {
    if (!game?.id) return;
    const loadParticipants = async () => {
      const { data } = await supabase
        .from('find_image_participants')
        .select('*')
        .eq('game_id', game.id);
      if (data) setParticipants(data);
    };
    loadParticipants();
  }, [game?.id]);

  // ==================== TIMER ====================

  useEffect(() => {
    if (view !== 'play' || !game?.started_at || !game?.time_limit_seconds) return;

    const updateTimer = () => {
      const elapsed = (Date.now() - new Date(game.started_at).getTime()) / 1000;
      const remaining = Math.max(0, game.time_limit_seconds - elapsed);
      setTimeRemaining(Math.ceil(remaining));

      if (remaining <= 0) {
        // Time's up -- end the game
        clearInterval(timerRef.current);
        timerRef.current = null;
        handleTimeUp();
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 250);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [view, game?.started_at, game?.time_limit_seconds]);

  const handleTimeUp = async () => {
    if (!game?.id || game.game_status === 'finished') return;
    // Only host should update the status
    if (user?.id === game.created_by) {
      await supabase
        .from('find_image_games')
        .update({
          game_status: 'finished',
          updated_at: new Date().toISOString()
        })
        .eq('id', game.id)
        .is('winner_name', null); // Only if no winner yet
    }
  };

  // ==================== HOST: CREATE GAME ====================

  const handleCreateGame = async () => {
    if (!user) return;
    setView('setup');
  };

  // Step 2: Generate scene image only (no target detection)
  const handleGenerateScene = async () => {
    if (!scenePrompt || !sceneName) return;

    setGenerating(true);
    setGenerationError(null);

    try {
      // Generate the scene with DALL-E 3 (no hidden object in prompt)
      console.log('[FIND-IMAGE] Generating scene...');
      const { url: dalleUrl } = await generateScene(scenePrompt, 'various interesting details');

      // Download and upload to Supabase Storage
      console.log('[FIND-IMAGE] Downloading and uploading to storage...');
      const imageBlob = await downloadImage(dalleUrl);
      const fileName = `scenes/${Date.now()}_${sceneName.replace(/\s+/g, '_')}.png`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('find-image-scenes')
        .upload(fileName, imageBlob, {
          cacheControl: '3600',
          contentType: 'image/png'
        });

      if (uploadError) throw new Error(`Storage upload failed: ${uploadError.message}`);

      const { data: { publicUrl } } = supabase.storage
        .from('find-image-scenes')
        .getPublicUrl(fileName);

      console.log('[FIND-IMAGE] Scene generated and uploaded:', publicUrl);
      setGeneratedImageUrl(publicUrl);
      setGeneratedImagePath(uploadData.path || fileName);

      // Save or update draft in DB
      const path = uploadData.path || fileName;
      if (draftGameId) {
        await supabase
          .from('find_image_games')
          .update({
            game_name: sceneName,
            scene_prompt: scenePrompt,
            image_url: publicUrl,
            image_path: path,
            setup_step: 2,
            updated_at: new Date().toISOString()
          })
          .eq('id', draftGameId);
      } else {
        const { data: draft, error: draftErr } = await supabase
          .from('find_image_games')
          .insert({
            game_name: sceneName,
            created_by: user.id,
            scene_prompt: scenePrompt,
            image_url: publicUrl,
            image_path: path,
            time_limit_seconds: timeLimit,
            game_status: 'setup',
            setup_step: 2
          })
          .select()
          .single();
        if (!draftErr && draft) {
          setDraftGameId(draft.id);
          console.log('[FIND-IMAGE] Draft saved:', draft.id);
        }
      }

    } catch (error) {
      console.error('[FIND-IMAGE] Generation error:', error);
      setGenerationError(error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Step 4: Create or finalize the game with overlay target
  const handleCreateGameWithTarget = async () => {
    if (!generatedImageUrl || !selectedTarget || !targetPosition) return;

    setGenerating(true);
    setGenerationError(null);

    try {
      const target = getTargetById(selectedTarget);
      const joinCode = generateJoinCode();
      let gameData;

      if (draftGameId) {
        // Finalize existing draft
        const { data, error } = await supabase
          .from('find_image_games')
          .update({
            join_code: joinCode,
            target_object: target.emoji + ' ' + target.name,
            target_bbox: targetPosition,
            time_limit_seconds: timeLimit,
            game_status: 'ready',
            setup_step: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', draftGameId)
          .select()
          .single();

        if (error) throw error;
        gameData = data;
      } else {
        // Create new game (no draft existed)
        const { data, error } = await supabase
          .from('find_image_games')
          .insert({
            game_name: sceneName,
            created_by: user.id,
            join_code: joinCode,
            scene_prompt: scenePrompt,
            target_object: target.emoji + ' ' + target.name,
            image_url: generatedImageUrl,
            image_path: generatedImagePath,
            target_bbox: targetPosition,
            time_limit_seconds: timeLimit,
            game_status: 'ready'
          })
          .select()
          .single();

        if (error) throw error;
        gameData = data;
      }

      // Add host as participant
      await supabase
        .from('find_image_participants')
        .insert({
          game_id: gameData.id,
          user_id: user.id,
          player_name: userProfile?.full_name || user.email,
          email: user.email
        });

      console.log('[FIND-IMAGE] Game created successfully:', gameData.id);
      setDraftGameId(null);
      setGame(gameData);
      setView('waiting');

    } catch (error) {
      console.error('[FIND-IMAGE] Game creation error:', error);
      setGenerationError(error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Randomize target position
  const handleRandomizePosition = () => {
    setTargetPosition(generateRandomPosition(0.045));
  };

  // ==================== HOST: START GAME ====================

  const handleStartGame = async () => {
    if (!game?.id) return;

    const { error } = await supabase
      .from('find_image_games')
      .update({
        game_status: 'active',
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', game.id);

    if (error) {
      console.error('[FIND-IMAGE] Error starting game:', error);
      return;
    }

    setView('play');
  };

  // ==================== PLAYER: JOIN GAME ====================

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || '').trim());

  const canJoinAsGuest =
    playerName.trim().length >= 2 && isValidEmail(playerEmail);

  const handleJoinGame = async (codeOverride) => {
    const code = (codeOverride || joinCodeInput || '').toUpperCase().trim();
    if (!code || code.length !== 6) return;

    // Must have either a signed-in user OR a name + valid email
    const hasAuth = !!user;
    if (!hasAuth && !canJoinAsGuest) return;

    setJoining(true);
    setJoinError(null);

    try {
      // Find the game
      const { data: gameData, error: findError } = await supabase
        .from('find_image_games')
        .select('*')
        .eq('join_code', code)
        .maybeSingle();

      if (findError) throw findError;
      if (!gameData) {
        setJoinError('Game not found. Check your join code.');
        return;
      }
      if (gameData.game_status === 'finished') {
        setJoinError('This game has already ended.');
        return;
      }
      if (gameData.game_status === 'active') {
        setJoinError('This game is already in progress.');
        return;
      }

      // Build participant record
      const participantData = {
        game_id: gameData.id,
        user_id: user?.id || null,
        player_name: user ? (userProfile?.full_name || user.email) : playerName.trim(),
        email: user ? user.email : playerEmail.trim().toLowerCase()
      };

      // Check if this email already joined (handles null user_id uniqueness)
      const { data: existing } = await supabase
        .from('find_image_participants')
        .select('id')
        .eq('game_id', gameData.id)
        .eq('email', participantData.email)
        .maybeSingle();

      if (!existing) {
        const { error: joinError } = await supabase
          .from('find_image_participants')
          .insert(participantData);

        if (joinError && joinError.code !== '23505') throw joinError;
      }

      setGame(gameData);
      setView('waiting');

    } catch (error) {
      console.error('[FIND-IMAGE] Join error:', error);
      setJoinError(error.message);
    } finally {
      setJoining(false);
    }
  };

  // ==================== PLAYER: CLICK ON IMAGE ====================

  const handleImageClick = useCallback(async (e) => {
    if (!game || game.game_status !== 'active' || !imageRef.current) return;
    if (game.winner_user_id || game.winner_name) return; // Already won

    const rect = imageRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / rect.width;
    const clickY = (e.clientY - rect.top) / rect.height;

    const bbox = game.target_bbox;
    if (!bbox) return;

    // Check if click is within the bounding box
    const isHit = (
      clickX >= bbox.x &&
      clickX <= bbox.x + bbox.width &&
      clickY >= bbox.y &&
      clickY <= bbox.y + bbox.height
    );

    if (isHit) {
      console.log('[FIND-IMAGE] TARGET FOUND!', { clickX, clickY, bbox });

      const winnerName = user
        ? (userProfile?.full_name || user.email)
        : (playerName || 'Guest');

      // Write winner to DB (race-condition safe: only if no winner yet)
      const { error } = await supabase
        .from('find_image_games')
        .update({
          winner_user_id: user?.id || null,
          winner_name: winnerName,
          won_at: new Date().toISOString(),
          game_status: 'finished',
          updated_at: new Date().toISOString()
        })
        .eq('id', game.id)
        .is('winner_user_id', null)
        .is('winner_name', null);

      if (error) {
        console.error('[FIND-IMAGE] Error recording winner:', error);
      }
    }
    // No feedback on wrong clicks (per spec)
  }, [game, user, userProfile, playerName]);

  // ==================== COPY JOIN CODE ====================

  const copyJoinCode = async () => {
    if (!game?.join_code) return;
    try {
      await navigator.clipboard.writeText(game.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = game.join_code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    if (seconds == null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ==================== SHARED STYLES ====================

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(215, 2, 246, 0.3)',
    borderRadius: '16px',
    padding: '2rem',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 0 20px rgba(215,2,246,0.2), 0 20px 45px rgba(0,0,0,0.3)'
  };

  const buttonPrimary = {
    fontSize: '1rem',
    fontWeight: 700,
    color: 'white',
    background: 'linear-gradient(120deg, #D702F6 0%, #8C2BFF 100%)',
    border: 'none',
    padding: '0.85rem 1.5rem',
    borderRadius: '12px',
    cursor: 'pointer',
    fontFamily: "'Orbitron', sans-serif",
    boxShadow: '0 8px 24px rgba(215, 2, 246, 0.4)',
    transition: 'all 0.25s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: '100%'
  };

  const buttonOutline = {
    ...buttonPrimary,
    background: 'transparent',
    border: '2px solid #D702F6',
    color: '#D702F6',
    boxShadow: '0 8px 24px rgba(215, 2, 246, 0.15)'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontFamily: "'Orbitron', sans-serif",
    color: 'white',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: "'Orbitron', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  // ==================== RENDER: JOINING (redirected with code, guest join) ====================

  if (view === 'lobby' && initialJoinCode && !user) {
    const emailTouched = playerEmail.trim().length > 0;
    const emailInvalid = emailTouched && !isValidEmail(playerEmail);
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        paddingTop: 'calc(60px + 5rem)'
      }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{
            ...cardStyle,
            padding: '2.5rem 2rem'
          }}>
            <h2 style={{
              fontSize: '1.4rem',
              fontWeight: 800,
              fontFamily: "'Orbitron', sans-serif",
              margin: '0 0 0.5rem'
            }}>
              Join the Game
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
              Enter your info to join with code <strong style={{ color: '#D702F6' }}>{initialJoinCode}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left' }}>
              <input
                type="text"
                placeholder="Your name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                style={{
                  ...inputStyle,
                  fontSize: '1rem'
                }}
              />
              <input
                type="email"
                placeholder="Your email"
                value={playerEmail}
                onChange={(e) => setPlayerEmail(e.target.value)}
                autoComplete="email"
                style={{
                  ...inputStyle,
                  fontSize: '1rem',
                  borderColor: emailInvalid ? 'rgba(255,107,107,0.7)' : 'rgba(255, 255, 255, 0.15)'
                }}
              />
              {emailInvalid && (
                <p style={{ color: '#ff6b6b', margin: 0, fontSize: '0.8rem' }}>
                  Enter a valid email address (e.g. name@example.com)
                </p>
              )}
            </div>
            <TextRevealButton
              disabled={!canJoinAsGuest}
              loading={joining}
              onClick={() => {
                handleJoinGame(initialJoinCode);
              }}
              style={{ marginTop: '1.25rem' }}
            >
              Join Game
            </TextRevealButton>
            {joinError && (
              <p style={{ color: '#ff6b6b', margin: '0.75rem 0 0', fontSize: '0.9rem' }}>{joinError}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== RENDER: AUTO-JOINING (signed in, waiting for join to complete) ====================

  if (view === 'lobby' && initialJoinCode && user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        paddingTop: 'calc(60px + 5rem)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#D702F6', marginBottom: '1rem' }} />
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', margin: 0 }}>
            Joining game...
          </p>
        </div>
      </div>
    );
  }

  // ==================== RENDER: LOBBY ====================

  if (view === 'lobby') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        padding: '2rem',
        paddingTop: 'calc(60px + 5rem)'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Bento grid layout */}
          <div style={{
            display: 'flex',
            gap: '1.25rem',
            alignItems: 'stretch'
          }}
            className="find-image-lobby-grid"
          >
            {/* Left: Hero image card - same image, scanner line and crosshair as home page */}
            <div
              ref={lobbyHeroRef}
              className="fi-lobby-hero"
              style={{
                flex: '1.4',
                minWidth: 0,
                position: 'relative',
                borderRadius: '18px',
                overflow: 'hidden',
                border: '1px solid rgba(215, 2, 246, 0.3)',
                boxShadow: '0 0 24px rgba(215,2,246,0.25), 0 20px 50px rgba(0,0,0,0.4)',
                minHeight: '420px',
                cursor: fiLobbyShowCrosshair ? 'none' : 'default'
              }}
              onMouseMove={(e) => {
                if (!lobbyHeroRef.current) return;
                const rect = lobbyHeroRef.current.getBoundingClientRect();
                setFiLobbyCursorPos({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                });
              }}
              onMouseEnter={() => setFiLobbyShowCrosshair(true)}
              onMouseLeave={() => setFiLobbyShowCrosshair(false)}
            >
              <div className="fi-lobby-hero__img" style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit' }}>
                <img
                  src="/images/find-image-card-bg.png"
                  alt="Find the Image"
                  className="fi-lobby-hero__img-el"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'brightness(0.45) contrast(0.9) saturate(0.85)',
                    transition: 'filter 0.4s ease'
                  }}
                />
              </div>
              {/* Animated scanner line - same as home page */}
              <div className="fi-lobby-hero__scanner" />
              {/* Gradient overlay for text readability */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(6,0,16,0.1) 0%, rgba(6,0,16,0.3) 50%, rgba(6,0,16,0.85) 100%)',
                pointerEvents: 'none',
                zIndex: 3
              }} />
              {/* Crosshair cursor - same as home page */}
              <div
                className={`fi-lobby-hero__crosshair ${fiLobbyShowCrosshair ? 'fi-lobby-hero__crosshair--visible' : ''}`}
                style={{
                  position: 'absolute',
                  left: fiLobbyCursorPos.x,
                  top: fiLobbyCursorPos.y,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 5,
                  pointerEvents: 'none'
                }}
              >
                <Crosshair size={48} strokeWidth={1.5} style={{ color: '#D702F6', filter: 'drop-shadow(0 0 10px rgba(215, 2, 246, 0.5))' }} />
              </div>
              {/* Text overlay */}
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                zIndex: 4
              }}>
                <span style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: "'Orbitron', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <Search size={14} /> Find The Image
                </span>
                <h1 style={{
                  fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
                  fontWeight: 900,
                  fontFamily: "'Orbitron', sans-serif",
                  margin: 0,
                  lineHeight: 1.15,
                  textShadow: '0 2px 12px rgba(0,0,0,0.8)'
                }}>
                  Be the First
                </h1>
                <p style={{
                  color: 'rgba(255,255,255,0.8)',
                  margin: 0,
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  textShadow: '0 2px 8px rgba(0,0,0,0.7)'
                }}>
                  Race to spot the hidden object in an AI-generated scene
                </p>
              </div>
            </div>

            {/* Right: Action cards stack */}
            <div style={{
              flex: '1',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem'
            }}>
              {/* Host: Create Game / Resume Draft */}
              {isHost() && (
                <div style={{
                  ...cardStyle,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {existingDraft ? (
                    <>
                      <h3 style={{
                        margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700,
                        fontFamily: "'Orbitron', sans-serif"
                      }}>
                        Continue Setup
                      </h3>
                      <p style={{ margin: '0 0 0.4rem', color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem' }}>
                        You have an unfinished game: <strong style={{ color: '#D702F6' }}>{existingDraft.game_name}</strong>
                      </p>
                      <p style={{ margin: '0 0 1.25rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                        Step {existingDraft.setup_step || 1} of 4
                      </p>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button onClick={() => handleResumeDraft(existingDraft)} style={{ ...buttonPrimary, flex: 1 }}>
                          Resume
                        </button>
                        <button
                          onClick={() => {
                            supabase
                              .from('find_image_games')
                              .delete()
                              .eq('id', existingDraft.id)
                              .then(() => {});
                            setExistingDraft(null);
                          }}
                          style={{
                            ...buttonPrimary,
                            flex: 1,
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.2)',
                            color: 'rgba(255,255,255,0.7)'
                          }}
                        >
                          Start Fresh
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 style={{
                        margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700,
                        fontFamily: "'Orbitron', sans-serif"
                      }}>
                        Host a Round
                      </h3>
                      <p style={{ margin: '0 0 1.25rem', color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem' }}>
                        Generate an AI scene with a hidden target for players to find
                      </p>
                      <button onClick={handleCreateGame} style={buttonPrimary}>
                        <Sparkles size={18} /> Create Game
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Join Game */}
              <div style={{
                ...cardStyle,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h3 style={{
                  margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700,
                  fontFamily: "'Orbitron', sans-serif"
                }}>
                  Join a Round
                </h3>
                <p style={{ margin: '0 0 1.25rem', color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem' }}>
                  Enter the code from your host
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="_ _ _ _ _ _"
                    maxLength={6}
                    className="join-code-input"
                    value={joinCodeInput}
                    onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      letterSpacing: '0.28em',
                      fontFamily: 'monospace',
                      color: '#D702F6',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '10px',
                      textTransform: 'uppercase',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  {!user && (
                    <>
                      <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                        Enter your name and email to join
                      </p>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        style={{
                          ...inputStyle,
                          fontSize: '0.95rem'
                        }}
                      />
                      <input
                        type="email"
                        placeholder="Your email"
                        value={playerEmail}
                        onChange={(e) => setPlayerEmail(e.target.value)}
                        autoComplete="email"
                        style={{
                          ...inputStyle,
                          fontSize: '0.95rem',
                          borderColor: playerEmail.trim() && !isValidEmail(playerEmail)
                            ? 'rgba(255,107,107,0.7)'
                            : 'rgba(255, 255, 255, 0.15)'
                        }}
                      />
                      {playerEmail.trim() && !isValidEmail(playerEmail) && (
                        <p style={{ color: '#ff6b6b', margin: 0, fontSize: '0.8rem' }}>
                          Enter a valid email address (e.g. name@example.com)
                        </p>
                      )}
                    </>
                  )}
                  <TextRevealButton
                    onClick={handleJoinGame}
                    disabled={joinCodeInput.length !== 6 || (!user && !canJoinAsGuest)}
                    loading={joining}
                    style={{ marginTop: '0.25rem' }}
                  >
                    Join
                  </TextRevealButton>
                </div>
                {joinError && (
                  <p style={{ color: '#ff6b6b', margin: '0.75rem 0 0', fontSize: '0.9rem' }}>{joinError}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scanner line + crosshair styles (match home page Find the Image showcase) */}
        <style>{`
          .fi-lobby-hero__scanner {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 100px;
            background: linear-gradient(
              90deg,
              transparent 0%,
              rgba(215, 2, 246, 0.04) 15%,
              rgba(215, 2, 246, 0.15) 40%,
              rgba(215, 2, 246, 0.35) 50%,
              rgba(215, 2, 246, 0.15) 60%,
              rgba(215, 2, 246, 0.04) 85%,
              transparent 100%
            );
            box-shadow: 0 0 40px rgba(215, 2, 246, 0.2);
            animation: fi-lobby-scanner-sweep 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            pointer-events: none;
            z-index: 2;
          }
          @keyframes fi-lobby-scanner-sweep {
            0% { left: -100px; }
            100% { left: calc(100% + 100px); }
          }
          .fi-lobby-hero__crosshair {
            opacity: 0;
            transition: opacity 0.15s ease;
          }
          .fi-lobby-hero__crosshair--visible {
            opacity: 1;
          }
          .fi-lobby-hero:hover .fi-lobby-hero__img-el {
            filter: brightness(0.55) contrast(0.95) saturate(1);
          }
          .fi-lobby-hero__crosshair svg {
            animation: fi-lobby-crosshair-rotate 8s linear infinite;
          }
          @keyframes fi-lobby-crosshair-rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @media (max-width: 768px) {
            .find-image-lobby-grid {
              flex-direction: column !important;
            }
          }
        `}</style>
      </div>
    );
  }

  // ==================== RENDER: SETUP (HOST) — FULL-PAGE FLOW (match Creative Coaster) ====================

  if (view === 'setup') {
    const categories = getCategories();
    const currentTarget = selectedTarget ? getTargetById(selectedTarget) : null;

    const setupOverlay = (
      <div
        className="fi-setup-fullpage"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: '#060010',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}
      >
        {/* X: back to Find The Image lobby (where they came from when clicking Create Game) */}
        <button
          type="button"
          onClick={() => setView('lobby')}
          aria-label="Close setup and return to Find the Image"
          style={{
            position: 'absolute',
            top: '1.25rem',
            right: '1.25rem',
            zIndex: 10,
            width: 44,
            height: 44,
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s, border-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
            e.currentTarget.style.borderColor = 'rgba(215, 2, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
          }}
        >
          <X size={22} strokeWidth={2.5} />
        </button>

        {/* Title and subtitle (match Creative Coaster setup flow) */}
        <div style={{ padding: '2rem 2rem 0', paddingRight: '4rem' }}>
          <h1 style={{
            margin: 0,
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            fontWeight: 800,
            fontFamily: "'Orbitron', sans-serif",
            color: '#ffffff',
            letterSpacing: '-0.02em',
            lineHeight: 1.2
          }}>
            Set Up New Find The Image Game
          </h1>
          <p style={{
            margin: '0.5rem 0 0',
            fontSize: '0.95rem',
            color: 'rgba(255,255,255,0.6)',
            lineHeight: 1.4
          }}>
            Follow the steps below to create your scene and hidden target for players to find.
          </p>
        </div>

        <div className="fi-setup-page" style={{ flex: 1, padding: '1.5rem 2rem 2rem', paddingRight: '4rem', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="fi-setup-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Stepper
            initialStep={1}
            step={setupStep}
            onStepChange={(step) => setSetupStep(step)}
            onFinalStepCompleted={handleCreateGameWithTarget}
            backButtonText="Previous"
            nextButtonText={setupStep === 1 ? 'Next' : (setupStep === 2 ? (generatedImageUrl ? 'Choose Target' : 'Next') : (setupStep === 3 ? 'Preview' : 'Create Game'))}
            hideFooter={true}
            disableStepIndicators={true}
            stepCircleContainerClassName="fi-stepper-container"
          >
          {/* ===== STEP 1: Scene Setup ===== */}
          <Step>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, fontFamily: "'Orbitron', sans-serif", margin: '0 0 0.25rem' }}>
              Set Up Your Scene
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 1.5rem', fontSize: '0.85rem' }}>
              Describe the scene that AI will generate for your players to search.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Game Name</label>
                <input
                  type="text"
                  placeholder="e.g., Friday Fun Round"
                  value={sceneName}
                  onChange={(e) => setSceneName(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Scene Description</label>
                <textarea
                  placeholder="e.g., a busy farmer's market on a sunny day with lots of people, stalls, and colorful produce"
                  value={scenePrompt}
                  onChange={(e) => setScenePrompt(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                />
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                  The more detail, the harder and more fun the game!
                </p>
              </div>

              <div>
                <label style={labelStyle}>Time Limit (seconds)</label>
                <input
                  type="number"
                  min={15}
                  max={300}
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(Math.max(15, Math.min(300, parseInt(e.target.value) || 60)))}
                  style={{ ...inputStyle, width: '120px' }}
                />
              </div>

              <button
                onClick={() => {
                  if (sceneName && scenePrompt) {
                    handleGenerateScene();
                    setSetupStep(2);
                  }
                }}
                disabled={!sceneName || !scenePrompt || generating}
                style={{
                  ...buttonPrimary,
                  opacity: !sceneName || !scenePrompt || generating ? 0.5 : 1,
                  cursor: !sceneName || !scenePrompt ? 'not-allowed' : 'pointer'
                }}
              >
                <Sparkles size={18} /> Generate Scene with AI
              </button>
            </div>
          </Step>

          {/* ===== STEP 2: Scene Preview / Generation ===== */}
          <Step>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, fontFamily: "'Orbitron', sans-serif", margin: '0 0 0.25rem' }}>
              Your Scene
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 1.25rem', fontSize: '0.85rem' }}>
              {generating ? 'AI is generating your scene...' : 'This is the scene your players will search through.'}
            </p>

            {generating && (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#D702F6', marginBottom: '1rem' }} />
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', margin: 0 }}>
                  Generating your scene...
                </p>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                  This may take 15-30 seconds
                </p>
              </div>
            )}

            {generationError && (
              <div style={{
                background: 'rgba(255, 80, 80, 0.1)',
                border: '1px solid rgba(255, 80, 80, 0.3)',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                color: '#ff6b6b',
                fontSize: '0.9rem',
                marginBottom: '1rem'
              }}>
                {generationError}
                <button
                  onClick={() => { setGenerationError(null); handleGenerateScene(); }}
                  style={{ ...buttonPrimary, marginTop: '0.75rem', fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                >
                  <RefreshCw size={14} /> Try Again
                </button>
              </div>
            )}

            {generatedImageUrl && !generating && (
              <>
                <div style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: '#000',
                  marginBottom: '1rem',
                  maxHeight: '50vh'
                }}>
                  <img
                    src={generatedImageUrl}
                    alt="Generated scene"
                    style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', paddingBottom: '1rem' }}>
                  <div style={{ flex: 1, position: 'relative' }} title={regenerationsUsed >= MAX_REGENERATIONS ? 'No regenerations remaining' : `${MAX_REGENERATIONS - regenerationsUsed} regeneration remaining`}>
                    <button
                      disabled={regenerationsUsed >= MAX_REGENERATIONS}
                      onClick={() => {
                        if (regenerationsUsed < MAX_REGENERATIONS) {
                          setRegenerationsUsed(prev => prev + 1);
                          setGeneratedImageUrl(null);
                          handleGenerateScene();
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.65rem',
                        background: regenerationsUsed >= MAX_REGENERATIONS ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
                        border: `1px solid ${regenerationsUsed >= MAX_REGENERATIONS ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '10px',
                        color: regenerationsUsed >= MAX_REGENERATIONS ? 'rgba(255,255,255,0.3)' : '#fff',
                        cursor: regenerationsUsed >= MAX_REGENERATIONS ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        opacity: regenerationsUsed >= MAX_REGENERATIONS ? 0.5 : 1
                      }}
                    >
                      <RefreshCw size={16} /> Regenerate {regenerationsUsed >= MAX_REGENERATIONS ? '(0 left)' : `(${MAX_REGENERATIONS - regenerationsUsed} left)`}
                    </button>
                  </div>
                  <button
                    onClick={() => setSetupStep(3)}
                    style={{ ...buttonPrimary, flex: 2 }}
                  >
                    Choose Target →
                  </button>
                </div>
              </>
            )}
          </Step>

          {/* ===== STEP 3: Choose Target ===== */}
          <Step>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, fontFamily: "'Orbitron', sans-serif", margin: '0 0 0.25rem' }}>
              Choose the Hidden Target
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 1.25rem', fontSize: '0.85rem' }}>
              Pick what players need to find. This will be placed on your scene.
            </p>

            {/* Category Tabs */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              marginBottom: '1.25rem'
            }}>
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  style={{
                    padding: '0.4rem 0.75rem',
                    borderRadius: '999px',
                    background: selectedCategory === cat.category ? 'rgba(215, 2, 246, 0.2)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${selectedCategory === cat.category ? 'rgba(215, 2, 246, 0.5)' : 'rgba(255,255,255,0.12)'}`,
                    color: selectedCategory === cat.category ? '#D702F6' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {cat.category}
                </button>
              ))}
            </div>

            {/* Target Grid */}
            {selectedCategory && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '0.75rem',
                marginBottom: '1.5rem'
              }}>
                {categories.find(c => c.category === selectedCategory)?.targets.map((target) => (
                  <button
                    key={target.id}
                    onClick={() => {
                      setSelectedTarget(target.id);
                      setTargetPosition(generateRandomPosition(0.045));
                    }}
                    style={{
                      padding: '1rem 0.5rem',
                      borderRadius: '12px',
                      background: selectedTarget === target.id ? 'rgba(215, 2, 246, 0.15)' : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${selectedTarget === target.id ? '#D702F6' : 'rgba(255,255,255,0.1)'}`,
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.4rem',
                      transition: 'all 0.2s ease',
                      transform: selectedTarget === target.id ? 'scale(1.03)' : 'scale(1)'
                    }}
                  >
                    <span style={{ fontSize: '2rem', lineHeight: 1 }}>{target.emoji}</span>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, opacity: 0.8 }}>{target.name}</span>
                  </button>
                ))}
              </div>
            )}

            {selectedTarget && (
              <button
                onClick={() => {
                  if (draftGameId) {
                    const target = getTargetById(selectedTarget);
                    supabase
                      .from('find_image_games')
                      .update({
                        target_object: target ? `${target.emoji} ${target.name}` : null,
                        setup_step: 4,
                        updated_at: new Date().toISOString()
                      })
                      .eq('id', draftGameId)
                      .then(() => {});
                  }
                  setSetupStep(4);
                }}
                style={buttonPrimary}
              >
                Preview Placement →
              </button>
            )}
          </Step>

          {/* ===== STEP 4: Preview & Confirm ===== */}
          <Step>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, fontFamily: "'Orbitron', sans-serif", margin: '0 0 0.25rem' }}>
              Preview & Create
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 1rem', fontSize: '0.85rem' }}>
              The {currentTarget?.name} has been placed. Shuffle to move it, or create the game!
            </p>

            {/* Preview Image with Target Overlay */}
            {generatedImageUrl && targetPosition && currentTarget && (
              <div style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)',
                background: '#000',
                marginBottom: '1rem'
              }}>
                <img
                  src={generatedImageUrl}
                  alt="Scene with target"
                  style={{ width: '100%', display: 'block', maxHeight: '420px', objectFit: 'contain' }}
                />
                {/* Target emoji overlay */}
                <div style={{
                  position: 'absolute',
                  left: `${targetPosition.x * 100}%`,
                  top: `${targetPosition.y * 100}%`,
                  width: `${targetPosition.width * 100}%`,
                  height: `${targetPosition.height * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(16px, 3vw, 32px)',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                  zIndex: 2
                }}>
                  {currentTarget.emoji}
                </div>
                {/* Host-only highlight box */}
                <div style={{
                  position: 'absolute',
                  left: `calc(${targetPosition.x * 100}% - 6px)`,
                  top: `calc(${targetPosition.y * 100}% - 6px)`,
                  width: `calc(${targetPosition.width * 100}% + 12px)`,
                  height: `calc(${targetPosition.height * 100}% + 12px)`,
                  border: '2px dashed rgba(215, 2, 246, 0.7)',
                  borderRadius: '6px',
                  pointerEvents: 'none',
                  zIndex: 3
                }} />
              </div>
            )}

            <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: '0 0 1rem' }}>
              Dashed box is only visible to you — players will only see the emoji in the scene
            </p>

            {generationError && (
              <div style={{
                background: 'rgba(255, 80, 80, 0.1)',
                border: '1px solid rgba(255, 80, 80, 0.3)',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                color: '#ff6b6b',
                fontSize: '0.9rem',
                marginBottom: '1rem'
              }}>
                {generationError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleRandomizePosition}
                style={{
                  flex: 1,
                  padding: '0.65rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem'
                }}
              >
                <RefreshCw size={16} /> Shuffle Position
              </button>
              <button
                onClick={handleCreateGameWithTarget}
                disabled={generating}
                style={{
                  ...buttonPrimary,
                  flex: 2,
                  opacity: generating ? 0.6 : 1
                }}
              >
                {generating ? (
                  <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</>
                ) : (
                  <><Sparkles size={18} /> Create Game</>
                )}
              </button>
            </div>
          </Step>
          </Stepper>
          </div>
        </div>
      </div>
    );

    return createPortal(setupOverlay, document.body);
  }

  // ==================== RENDER: WAITING ROOM ====================

  if (view === 'waiting') {
    const isGameHost = user?.id === game?.created_by;

    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        padding: '2rem',
        paddingTop: 'calc(60px + 5rem)'
      }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          {/* Top row: Game Info + Join Code side by side */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            {/* Left card: Game info */}
            <div style={{ ...cardStyle, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h2 style={{
                fontSize: '1.4rem', fontWeight: 800,
                fontFamily: "'Orbitron', sans-serif",
                margin: '0 0 0.5rem'
              }}>
                {game?.game_name || 'Waiting Room'}
              </h2>
              {isGameHost ? (
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.95rem' }}>
                  Find the: <strong style={{ color: '#D702F6' }}>{game?.target_object}</strong>
                </p>
              ) : (
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.95rem' }}>
                  Target will be revealed when the game starts
                </p>
              )}
            </div>

            {/* Right card: Join Code */}
            <div style={{ ...cardStyle, flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ margin: '0 0 0.4rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Join Code
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                <span style={{
                  fontSize: '2rem', fontWeight: 900, letterSpacing: '0.2em',
                  fontFamily: 'monospace', color: '#D702F6'
                }}>
                  {game?.join_code}
                </span>
                <button
                  onClick={copyJoinCode}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    color: 'white',
                    display: 'flex'
                  }}
                >
                  {copied ? <Check size={18} color="#4ade80" /> : <Copy size={18} />}
                </button>
              </div>
            </div>
          </div>

          {/* Host Preview — Full-size view matching player experience */}
          {isGameHost && game?.image_url && (
            <div style={{ ...cardStyle, marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Eye size={16} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Host Preview
                </span>
              </div>
              <div style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)',
                background: '#000'
              }}>
                <img
                  src={game.image_url}
                  alt="Scene preview"
                  style={{
                    width: '100%',
                    display: 'block',
                    maxHeight: 'calc(100vh - 280px)',
                    objectFit: 'contain'
                  }}
                />
                {/* Show target emoji + highlight box for host */}
                {game.target_bbox && (
                  <>
                    {/* Emoji target overlay */}
                    <div style={{
                      position: 'absolute',
                      left: `${game.target_bbox.x * 100}%`,
                      top: `${game.target_bbox.y * 100}%`,
                      width: `${game.target_bbox.width * 100}%`,
                      height: `${game.target_bbox.height * 100}%`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(16px, 3vw, 32px)',
                      pointerEvents: 'none',
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))',
                      zIndex: 2
                    }}>
                      {game.target_object?.split(' ')[0]}
                    </div>
                    {/* Host-only highlight box */}
                    <div style={{
                      position: 'absolute',
                      left: `calc(${game.target_bbox.x * 100}% - 6px)`,
                      top: `calc(${game.target_bbox.y * 100}% - 6px)`,
                      width: `calc(${game.target_bbox.width * 100}% + 12px)`,
                      height: `calc(${game.target_bbox.height * 100}% + 12px)`,
                      border: '2.5px dashed rgba(215, 2, 246, 0.7)',
                      borderRadius: '6px',
                      boxShadow: '0 0 16px rgba(215, 2, 246, 0.5), inset 0 0 12px rgba(215, 2, 246, 0.1)',
                      pointerEvents: 'none',
                      zIndex: 3
                    }} />
                    {/* Label above the bbox */}
                    <div style={{
                      position: 'absolute',
                      left: `${game.target_bbox.x * 100}%`,
                      top: `${game.target_bbox.y * 100}%`,
                      transform: 'translateY(calc(-100% - 6px))',
                      background: 'rgba(215, 2, 246, 0.9)',
                      color: '#000',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none'
                    }}>
                      {game.target_object}
                    </div>
                  </>
                )}
              </div>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                This is what players will see — purple box and label are only visible to you
              </p>
            </div>
          )}

          {/* Players */}
          {(() => {
            const nonHostPlayers = participants.filter(p => p.user_id !== game?.created_by);
            return (
              <div style={{ ...cardStyle, marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <Users size={16} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Players ({nonHostPlayers.length})
                  </span>
                </div>
                {nonHostPlayers.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '0.9rem' }}>
                    Waiting for players to join...
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {nonHostPlayers.map((p) => (
                      <span
                        key={p.id}
                        style={{
                          padding: '0.4rem 0.75rem',
                          borderRadius: '999px',
                          background: 'rgba(215, 2, 246, 0.15)',
                          border: '1px solid rgba(215, 2, 246, 0.3)',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.9)'
                        }}
                      >
                        {p.player_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Start Button (host only) */}
          {isGameHost && (
            <button
              onClick={handleStartGame}
              disabled={participants.length < 1}
              style={{
                ...buttonPrimary,
                opacity: participants.length < 1 ? 0.5 : 1,
                cursor: participants.length < 1 ? 'not-allowed' : 'pointer',
                fontSize: '1.1rem',
                padding: '1rem'
              }}
            >
              Start Game
            </button>
          )}

          {!isGameHost && (
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.95rem'
            }}>
              Waiting for host to start the game...
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== RENDER: PLAY ====================

  if (view === 'play') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Top bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.5rem',
          paddingTop: 'calc(60px + 0.75rem)',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(215, 2, 246, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Search size={18} color="#D702F6" />
            <span style={{
              fontSize: '0.95rem', fontWeight: 700,
              fontFamily: "'Orbitron', sans-serif"
            }}>
              Find: <span style={{ color: '#D702F6' }}>{game?.target_object}</span>
            </span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: timeRemaining != null && timeRemaining <= 10 ? 'rgba(255, 80, 80, 0.15)' : 'rgba(215, 2, 246, 0.08)',
            padding: '0.35rem 0.65rem',
            borderRadius: '8px',
            border: `1px solid ${timeRemaining != null && timeRemaining <= 10 ? 'rgba(255, 80, 80, 0.4)' : 'rgba(215, 2, 246, 0.3)'}`
          }}>
            <NeonCountdown
              size="sm"
              seconds={timeRemaining != null ? timeRemaining : 0}
              tone={timeRemaining != null && timeRemaining <= 10 ? 'urgent' : 'default'}
            />
          </div>
        </div>

        {/* Game image with target overlay */}
        <div
          ref={gameAreaRef}
          onMouseMove={(e) => {
            const rect = gameAreaRef.current.getBoundingClientRect();
            setGameCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
          onMouseEnter={() => setShowGameCrosshair(true)}
          onMouseLeave={() => setShowGameCrosshair(false)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            cursor: 'none',
            position: 'relative'
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: gameCursorPos.x,
              top: gameCursorPos.y,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 10,
              opacity: showGameCrosshair ? 1 : 0,
              transition: 'opacity 0.15s ease'
            }}
          >
            <Crosshair
              size={48}
              stroke="#D702F6"
              strokeWidth={1.5}
              style={{
                filter: 'drop-shadow(0 0 10px rgba(215, 2, 246, 0.5))',
                animation: 'fi-crosshair-rotate 8s linear infinite'
              }}
            />
          </div>
          {game?.image_url && (
            <div
              ref={imageRef}
              onClick={handleImageClick}
              style={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 0 40px rgba(0,0,0,0.5)'
              }}
            >
              <img
                src={game.image_url}
                alt="Find the hidden object"
                onLoad={() => setImageLoaded(true)}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 140px)',
                  objectFit: 'contain',
                  display: 'block',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              />
              {/* Emoji target overlay */}
              {game.target_bbox && imageLoaded && (
                <div style={{
                  position: 'absolute',
                  left: `${game.target_bbox.x * 100}%`,
                  top: `${game.target_bbox.y * 100}%`,
                  width: `${game.target_bbox.width * 100}%`,
                  height: `${game.target_bbox.height * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(16px, 3vw, 32px)',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))',
                  zIndex: 2
                }}>
                  {game.target_object?.split(' ')[0]}
                </div>
              )}
            </div>
          )}
          {!imageLoaded && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.5)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              Loading image...
            </div>
          )}
        </div>
        <style>{`
          @keyframes fi-crosshair-rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ==================== RENDER: RESULTS ====================

  if (view === 'results') {
    const hasWinner = !!(game?.winner_user_id || game?.winner_name);
    const isWinner = user
      ? game?.winner_user_id === user.id
      : game?.winner_name === playerName;

    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        color: 'white',
        padding: '2rem',
        paddingTop: 'calc(60px + 5rem)'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {/* Result header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            {hasWinner ? (
              <>
                <Trophy size={48} color="#facc15" style={{ marginBottom: '0.75rem' }} />
                <h2 style={{
                  fontSize: '1.8rem', fontWeight: 900,
                  fontFamily: "'Orbitron', sans-serif",
                  margin: '0 0 0.5rem',
                  color: isWinner ? '#facc15' : 'white'
                }}>
                  {isWinner ? 'You Found It!' : `${game.winner_name} Wins!`}
                </h2>
                {game.won_at && game.started_at && (
                  <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '1rem' }}>
                    Found in {((new Date(game.won_at) - new Date(game.started_at)) / 1000).toFixed(1)} seconds
                  </p>
                )}
              </>
            ) : (
              <>
                <Clock size={48} color="#ff6b6b" style={{ marginBottom: '0.75rem' }} />
                <h2 style={{
                  fontSize: '1.8rem', fontWeight: 900,
                  fontFamily: "'Orbitron', sans-serif",
                  margin: '0 0 0.5rem'
                }}>
                  Time's Up!
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                  Nobody found the {game?.target_object}
                </p>
              </>
            )}
          </div>

          {/* Reveal image with target highlighted */}
          {game?.image_url && (
            <div style={{ ...cardStyle, marginBottom: '1.5rem', padding: '1rem' }}>
              <p style={{
                margin: '0 0 0.75rem', textAlign: 'center',
                fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)',
                textTransform: 'uppercase', letterSpacing: '0.1em'
              }}>
                The {game.target_object} was here
              </p>
              <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                <img
                  src={game.image_url}
                  alt="Scene with target revealed"
                  style={{ width: '100%', display: 'block', borderRadius: '10px' }}
                />
                {game.target_bbox && (
                  <>
                    {/* Dim overlay everywhere except target */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      pointerEvents: 'none',
                      clipPath: `polygon(
                        0% 0%, 100% 0%, 100% 100%, 0% 100%,
                        0% ${game.target_bbox.y * 100}%,
                        ${game.target_bbox.x * 100}% ${game.target_bbox.y * 100}%,
                        ${game.target_bbox.x * 100}% ${(game.target_bbox.y + game.target_bbox.height) * 100}%,
                        ${(game.target_bbox.x + game.target_bbox.width) * 100}% ${(game.target_bbox.y + game.target_bbox.height) * 100}%,
                        ${(game.target_bbox.x + game.target_bbox.width) * 100}% ${game.target_bbox.y * 100}%,
                        0% ${game.target_bbox.y * 100}%
                      )`
                    }} />
                    {/* Highlight border around target */}
                    <div style={{
                      position: 'absolute',
                      left: `${game.target_bbox.x * 100}%`,
                      top: `${game.target_bbox.y * 100}%`,
                      width: `${game.target_bbox.width * 100}%`,
                      height: `${game.target_bbox.height * 100}%`,
                      border: '3px solid #facc15',
                      borderRadius: '6px',
                      boxShadow: '0 0 20px rgba(250, 204, 21, 0.6), inset 0 0 20px rgba(250, 204, 21, 0.1)',
                      pointerEvents: 'none',
                      animation: 'pulse-glow 2s ease-in-out infinite'
                    }} />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Back to lobby */}
          <button
            onClick={() => { setGame(null); setView('lobby'); setImageLoaded(false); }}
            style={buttonOutline}
          >
            <ArrowLeft size={18} /> Back to Lobby
          </button>
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(250, 204, 21, 0.6), inset 0 0 20px rgba(250, 204, 21, 0.1); }
            50% { box-shadow: 0 0 35px rgba(250, 204, 21, 0.9), inset 0 0 30px rgba(250, 204, 21, 0.2); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return null;
}

export default FindImageGame;
